import { useRef, useState } from "react";
import { Upload, Image as ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { files as filesApi } from "../lib/api";

export default function FileUploader({
  auditId = "",
  kind = "attachment",
  accept,
  multiple = false,
  onUploaded,
  label,
  testid = "file-uploader",
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const isPhoto = kind === "photo";
  const acceptDefault = isPhoto ? "image/*" : ".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of list) {
        if (f.size > 5 * 1024 * 1024) {
          toast.warning(t("common.file_too_big", { name: f.name }));
        }
        const rec = await filesApi.upload(f, auditId, kind);
        uploaded.push(rec);
      }
      onUploaded?.(uploaded);
      toast.success(t("common.files_uploaded", { n: uploaded.length }));
    } catch (err) {
      console.error(err);
      toast.error(t("common.upload_error"));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept || acceptDefault}
        multiple={multiple}
        capture={isPhoto ? "environment" : undefined}
        className="hidden"
        onChange={handle}
        data-testid={`${testid}-input`}
      />
      <Button
        variant="outline"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="gap-2"
        data-testid={`${testid}-button`}
      >
        {isPhoto ? <ImageIcon size={16} /> : <Upload size={16} />}
        {busy ? t("common.uploading") : label || (isPhoto ? t("common.take_photo") : t("common.attach_file"))}
      </Button>
    </>
  );
}
